import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Upload,
  message,
  Popconfirm,
  Space,
  Typography,
  Row,
  Col,
  Statistic,
  Empty,
  Spin,
  Avatar,
  Tag,
  Tooltip,
  Select,
} from 'antd';
import {
  UserAddOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  MedicineBoxOutlined,
  UserOutlined,
  UploadOutlined,
  StarOutlined,
} from '@ant-design/icons';
import { doctorService } from '../../services/doctorService';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const DoctorManagement = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [fileList, setFileList] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await doctorService.getAllDoctors();
      setDoctors(data);
    } catch (error) {
      console.error(error);
      message.error('Fehler beim Laden der Ärzte');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (values) => {
    try {
      const doctorData = {
        firstName: values.firstName,
        lastName: values.lastName,
        title: values.title,
        specialty: values.specialty,
        description: values.description,
        qualifications: values.qualifications,
        profilePhoto: fileList.length > 0 ? fileList[0].originFileObj : null,
      };

      await doctorService.createDoctor(doctorData);
      message.success('Arzt erfolgreich hinzugefügt');
      setModalVisible(false);
      fetchData();
      form.resetFields();
      setFileList([]);
    } catch (error) {
      if (error.response) {
        message.error(error.response.data.error || 'Fehler beim Hinzufügen des Arztes');
      } else {
        message.error('Fehler beim Hinzufügen des Arztes');
      }
    }
  };

  const handleEdit = (record) => {
    setEditingDoctor(record);
    form.setFieldsValue({
      firstName: record.firstName,
      lastName: record.lastName,
      title: record.title,
      specialty: record.specialty,
      description: record.description,
      qualifications: record.qualifications,
    });
    
    // Clear file list when editing - user can upload new photo if needed
    setFileList([]);
    setModalVisible(true);
  };

  const handleUpdate = async (values) => {
    try {
      console.log('Current fileList:', fileList);
      console.log('FileList length:', fileList.length);
      
      const doctorData = {
        firstName: values.firstName,
        lastName: values.lastName,
        title: values.title,
        specialty: values.specialty,
        description: values.description,
        qualifications: values.qualifications,
        // Only include profilePhoto if a new file was selected
        profilePhoto: fileList.length > 0 && fileList[0].originFileObj ? fileList[0].originFileObj : null,
      };

      console.log('Updating doctor with data:', {
        ...doctorData,
        profilePhoto: doctorData.profilePhoto ? 'File selected: ' + doctorData.profilePhoto.name : 'No new file'
      });

      await doctorService.updateDoctor(editingDoctor._id, doctorData);
      message.success('Arzt erfolgreich aktualisiert');
      setModalVisible(false);
      setEditingDoctor(null);
      form.resetFields();
      setFileList([]);
      fetchData();
    } catch (error) {
      console.error('Update error:', error);
      if (error.response) {
        message.error(error.response.data.error || 'Fehler beim Aktualisieren des Arztes');
      } else {
        message.error('Fehler beim Aktualisieren des Arztes');
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      await doctorService.deleteDoctor(id);
      message.success('Arzt erfolgreich gelöscht');
      fetchData();
    } catch (error) {
      message.error('Fehler beim Löschen des Arztes');
    }
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setEditingDoctor(null);
    form.resetFields();
    setFileList([]);
  };

  const uploadProps = {
    beforeUpload: (file) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('Sie können nur Bilddateien hochladen!');
        return false;
      }
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('Bild muss kleiner als 10MB sein!');
        return false;
      }
      
      console.log('File selected:', file);
      setFileList([{
        uid: file.uid || Date.now().toString(),
        name: file.name,
        status: 'done',
        originFileObj: file,
      }]);
      message.success(`${file.name} ausgewählt`);
      return false;
    },
    fileList,
    onRemove: () => {
      console.log('File removed');
      setFileList([]);
      message.info('Datei entfernt');
    },
    maxCount: 1,
    showUploadList: true,
  };

  // Statistics
  const stats = {
    total: doctors.length,
    active: doctors.filter(doctor => doctor.isActive !== false).length,
  };

  // Table columns
  const columns = [
    {
      title: 'Foto',
      dataIndex: 'profilePhoto',
      key: 'profilePhoto',
      render: (photo, record) => (
        <Avatar
          size={64}
          src={photo}
          icon={<UserOutlined />}
          style={{ border: '2px solid #f0f0f0' }}
        />
      ),
      width: 80,
    },
    {
      title: 'Name',
      key: 'fullName',
      render: (_, record) => (
        <div>
          <Text strong style={{ fontSize: '16px' }}>
            {record.fullName || `${record.title || ''} ${record.firstName} ${record.lastName}`.trim()}
          </Text>
          <br />
          <Text type="secondary">{record.title}</Text>
        </div>
      ),
      sorter: (a, b) => (a.lastName || '').localeCompare(b.lastName || ''),
    },
    {
      title: 'Fachgebiet',
      dataIndex: 'specialty',
      key: 'specialty',
      width: 200,
      render: (specialty) => (
        <Tag 
          color="blue" 
          style={{ 
            fontSize: '14px',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            maxWidth: '180px',
            height: 'auto',
            lineHeight: '1.4',
            padding: '4px 8px'
          }}
        >
          <MedicineBoxOutlined style={{ marginRight: 4 }} />
          {specialty}
        </Tag>
      ),
    },
    {
      title: 'Qualifikationen',
      dataIndex: 'qualifications',
      key: 'qualifications',
      render: (qualifications) => (
        <div>
          {qualifications && qualifications.length > 0 ? (
            qualifications.map((qual, index) => (
              <Tag key={index} color="green" style={{ marginBottom: 4 }}>
                {qual}
              </Tag>
            ))
          ) : (
            <Text type="secondary">Keine Angaben</Text>
          )}
        </div>
      ),
    },
    {
      title: 'Beschreibung',
      dataIndex: 'description',
      key: 'description',
      render: (description) => (
        <Text type="secondary" style={{ fontSize: '14px' }}>
          {description && description.length > 100 
            ? `${description.substring(0, 100)}...` 
            : description || 'Keine Beschreibung'}
        </Text>
      ),
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive !== false ? 'green' : 'red'}>
          {isActive !== false ? 'Aktiv' : 'Inaktiv'}
        </Tag>
      ),
    },
    {
      title: 'Aktionen',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Details anzeigen">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => {
                Modal.info({
                  title: record.fullName || `${record.firstName} ${record.lastName}`,
                  content: (
                    <div style={{ padding: 12 }}>
                      <div style={{ marginBottom: 16 }}>
                        <Avatar
                          size={80}
                          src={record.profilePhoto}
                          icon={<UserOutlined />}
                        />
                      </div>
                      <div>
                        <p><strong>Titel:</strong> {record.title || 'Nicht angegeben'}</p>
                        <p><strong>Fachgebiet:</strong> {record.specialty}</p>
                        <p><strong>Beschreibung:</strong></p>
                        <div style={{ whiteSpace: 'pre-wrap', background: '#f5f5f5', padding: 12, borderRadius: 6 }}>
                          {record.description || 'Keine Beschreibung vorhanden'}
                        </div>
                        {record.qualifications && record.qualifications.length > 0 && (
                          <>
                            <p style={{ marginTop: 16 }}><strong>Qualifikationen:</strong></p>
                            <div>
                              {record.qualifications.map((qual, index) => (
                                <Tag key={index} color="green" style={{ marginBottom: 4 }}>
                                  {qual}
                                </Tag>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ),
                  width: 600
                });
              }}
            />
          </Tooltip>
          <Tooltip title="Bearbeiten">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Möchten Sie diesen Arzt wirklich löschen?"
            onConfirm={() => handleDelete(record._id)}
            okText="Ja"
            cancelText="Nein"
            icon={<DeleteOutlined style={{ color: 'red' }} />}
          >
            <Tooltip title="Löschen">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px', background: 'var(--app-bg, #f5f5f5)', minHeight: '100vh' }}>
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Title level={2} style={{ margin: 0 }}>
              <MedicineBoxOutlined style={{ marginRight: 8 }} />
              Ärzteverwaltung
            </Title>
            <Text type="secondary">Verwalten Sie die Ärzte Ihrer Praxis</Text>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              size="large"
              onClick={() => setModalVisible(true)}
            >
              Arzt hinzufügen
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Statistics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card>
            <Statistic
              title="Gesamtanzahl Ärzte"
              value={stats.total}
              prefix={<MedicineBoxOutlined />}
              valueStyle={{ color: '#41b06e' }}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card>
            <Statistic
              title="Aktive Ärzte"
              value={stats.active}
              prefix={<StarOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Doctors Table */}
      <Card>
        <Spin spinning={loading}>
          {doctors.length === 0 ? (
            <Empty
              description="Keine Ärzte gefunden"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <Button type="primary" onClick={() => setModalVisible(true)}>
                Ersten Arzt hinzufügen
              </Button>
            </Empty>
          ) : (
            <Table
              columns={columns}
              dataSource={doctors}
              rowKey="_id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} von ${total} Einträgen`
              }}
              scroll={{ x: 1400 }}
            />
          )}
        </Spin>
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingDoctor ? 'Arzt bearbeiten' : 'Neuen Arzt hinzufügen'}
        open={modalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={700}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={editingDoctor ? handleUpdate : handleSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label="Vorname"
                rules={[{ required: true, message: 'Bitte geben Sie den Vornamen ein' }]}
              >
                <Input placeholder="Vorname eingeben" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="lastName"
                label="Nachname"
                rules={[{ required: true, message: 'Bitte geben Sie den Nachnamen ein' }]}
              >
                <Input placeholder="Nachname eingeben" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="title"
                label="Titel (z.B. Dr.med.)"
              >
                <Input placeholder="Dr.med., Dr., Prof. Dr., etc." />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="specialty"
                label="Fachgebiet"
                rules={[{ required: true, message: 'Bitte geben Sie das Fachgebiet ein' }]}
              >
                <Select placeholder="Fachgebiet auswählen" allowClear>
                  <Option value="Allgemeinmedizin">Allgemeinmedizin</Option>
                  <Option value="Innere Medizin">Innere Medizin</Option>
                  <Option value="Palliativmedizin">Palliativmedizin</Option>
                  <Option value="Kardiologie">Kardiologie</Option>
                  <Option value="Gastroenterologie">Gastroenterologie</Option>
                  <Option value="Pneumologie">Pneumologie</Option>
                  <Option value="Neurologie">Neurologie</Option>
                  <Option value="Orthopädie">Orthopädie</Option>
                  <Option value="Dermatologie">Dermatologie</Option>
                  <Option value="Gynäkologie">Gynäkologie</Option>
                  <Option value="Urologie">Urologie</Option>
                  <Option value="HNO">HNO</Option>
                  <Option value="Augenheilkunde">Augenheilkunde</Option>
                  <Option value="Chirurgie">Chirurgie</Option>
                  <Option value="Anästhesie">Anästhesie</Option>
                  <Option value="Radiologie">Radiologie</Option>
                  <Option value="Pathologie">Pathologie</Option>
                  <Option value="Psychiatrie">Psychiatrie</Option>
                  <Option value="Psychosomatik">Psychosomatik</Option>
                  <Option value="Pädiatrie">Pädiatrie</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="qualifications"
            label="Qualifikationen"
          >
            <Select
              mode="tags"
              placeholder="Qualifikationen eingeben (Enter drücken um hinzuzufügen)"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Beschreibung"
          >
            <TextArea
              rows={4}
              placeholder="Beschreibung des Arztes, Spezialisierungen, etc."
              showCount
              maxLength={1000}
            />
          </Form.Item>

          <Form.Item
            label="Profilbild"
          >
            {editingDoctor && editingDoctor.profilePhoto && (
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary">Aktuelles Bild:</Text>
                <br />
                <Avatar
                  size={80}
                  src={editingDoctor.profilePhoto}
                  icon={<UserOutlined />}
                  style={{ margin: '8px 0' }}
                />
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Laden Sie ein neues Bild hoch, um es zu ersetzen
                </Text>
              </div>
            )}
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />}>
                {editingDoctor ? 'Neues Bild hochladen (max. 10MB)' : 'Bild hochladen (max. 10MB)'}
              </Button>
            </Upload>
            {fileList.length > 0 && fileList[0] && (
              <div style={{ marginTop: 8 }}>
                <Text type="success" style={{ display: 'block', fontSize: '12px' }}>
                  ✓ Neues Bild ausgewählt: {fileList[0].name}
                </Text>
                <Text type="secondary" style={{ display: 'block', fontSize: '10px' }}>
                  Datei bereit zum Upload: {(fileList[0].originFileObj?.size / 1024 / 1024).toFixed(2)} MB
                </Text>
              </div>
            )}
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleModalClose}>
                Abbrechen
              </Button>
              <Button type="primary" htmlType="submit">
                {editingDoctor ? 'Aktualisieren' : 'Erstellen'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DoctorManagement;
